import { groupLayerMarkers } from '../modules/layer-screen-clusters';
import { drillDownTransactionCluster } from '../modules/transaction-drilldown';
import GeoJsonFetchLayer from './GeoJsonFetchLayer';
import { MarkerGroup, createHtmlMarker } from '../utils/markers';
import { addOrUpdateGeoJsonSource, removeSourceAndLayers } from '../utils/maplibre-helpers';

var EARTH_RADIUS_METERS = 6371000;

function buildRadiusPolygon(lng, lat, radiusMeters, pointCount) {
    var originLat = lat * Math.PI / 180;
    var originLng = lng * Math.PI / 180;
    var angularDistance = radiusMeters / EARTH_RADIUS_METERS;
    var coordinates = [];
    var totalPoints = Math.max(24, pointCount || 64);

    for (var index = 0; index <= totalPoints; index += 1) {
        var bearing = (index / totalPoints) * Math.PI * 2;
        var nextLat = Math.asin(
            Math.sin(originLat) * Math.cos(angularDistance)
            + Math.cos(originLat) * Math.sin(angularDistance) * Math.cos(bearing)
        );
        var nextLng = originLng + Math.atan2(
            Math.sin(bearing) * Math.sin(angularDistance) * Math.cos(originLat),
            Math.cos(angularDistance) - Math.sin(originLat) * Math.sin(nextLat)
        );

        coordinates.push([
            ((nextLng * 180 / Math.PI + 540) % 360) - 180,
            nextLat * 180 / Math.PI,
        ]);
    }

    return {
        type: 'FeatureCollection',
        features: [{
            type: 'Feature',
            geometry: {
                type: 'Polygon',
                coordinates: [coordinates],
            },
        }],
    };
}

function normalizeGeoJsonToFeatureCollection(geojson) {
    if (!geojson || typeof geojson !== 'object' || !geojson.type) {
        return null;
    }

    if (geojson.type === 'FeatureCollection') {
        return geojson;
    }

    if (geojson.type === 'Feature') {
        return {
            type: 'FeatureCollection',
            features: [geojson],
        };
    }

    return {
        type: 'FeatureCollection',
        features: [{
            type: 'Feature',
            geometry: geojson,
            properties: {},
        }],
    };
}

function buildPointHighlightFeatureCollection(lng, lat) {
    return {
        type: 'FeatureCollection',
        features: [{
            type: 'Feature',
            geometry: {
                type: 'Point',
                coordinates: [lng, lat],
            },
            properties: {},
        }],
    };
}

function getSentimentTone(sentiment) {
    if (sentiment === 'pro') {
        return {
            marker: '#10d758',
            accent: '#10d758',
            fill: 'rgba(107,143,114,0.18)',
            line: '#5f7f66',
            shadow: 'rgba(29,78,52,0.18)',
        };
    }

    if (sentiment === 'con') {
        return {
            marker: '#e15759',
            accent: '#e15759',
            fill: 'rgba(181,110,84,0.18)',
            line: '#a86046',
            shadow: 'rgba(124,45,18,0.18)',
        };
    }

    return {
        marker: 'rgba(103,118,134,0.92)',
        accent: '#677686',
        fill: 'rgba(103,118,134,0.18)',
        line: '#5c6978',
        shadow: 'rgba(51,65,85,0.18)',
    };
}

function getHighlightColorsForSignal(signal) {
    var sentiment = signal && signal.category ? signal.category.sentiment : null;
    var tone = getSentimentTone(sentiment);

    return {
        fill: tone.fill,
        line: tone.line,
        point: tone.accent,
        halo: 'rgba(255,255,255,0.94)',
        casing: 'rgba(15,23,42,0.72)',
        shadow: tone.shadow,
    };
}

function getRadiusColorsForSignal(signal) {
    var colors = getHighlightColorsForSignal(signal);

    return {
        fill: colors.fill,
        line: colors.line,
    };
}

function resolveSignalsAggDotColor(proCount, conCount) {
    if (proCount > 0 && conCount === 0) {
        return SignalsLayer.AGGREGATED_PRO_COLOR;
    }
    if (conCount > 0 && proCount === 0) {
        return SignalsLayer.AGGREGATED_CON_COLOR;
    }
    return SignalsLayer.AGGREGATED_MARKER_COLOR;
}

function buildSignalsAggDotHtml(count, color) {
    var size = count < 10 ? 11 : count < 100 ? 14 : count < 1000 ? 17 : 20;
    var diameter = size * 2;
    return '<div class="nm-agg-dot nm-agg-dot--signals" style="width:'
        + diameter + 'px;height:' + diameter + 'px;background:' + color + '">'
        + '<span class="nm-agg-count">' + count + '</span></div>';
}

function buildSignalsAggPillHtml(proCount, conCount) {
    return '<div class="nm-combined-badge nm-combined-badge--signals">'
        + '<span class="nm-combined-badge-signals-pro">' + proCount + '</span>'
        + '<span class="nm-combined-badge-sep"></span>'
        + '<span class="nm-combined-badge-signals-con">' + conCount + '</span>'
        + '</div>';
}

export default class SignalsLayer extends GeoJsonFetchLayer {
    static ID = 'sygnaly';
    static DESCRIPTION = 'Sygnały społeczności';
    static MIN_ZOOM = 0;
    static INDIVIDUAL_ZOOM_THRESHOLD = 14;
    static SOURCE_ID = 'src-signals';
    static BASE_LAYER_IDS = [
        'lyr-signals-main-circle',
        'lyr-signals-icon',
        'lyr-signals-count-circle',
        'lyr-signals-count-text',
    ];
    static HIGHLIGHT_SOURCE_ID = 'src-signals-highlight';
    static HIGHLIGHT_LAYER_IDS = [
        'lyr-signals-highlight-fill',
        'lyr-signals-highlight-line',
        'lyr-signals-highlight-linestring-casing',
        'lyr-signals-highlight-linestring',
        'lyr-signals-highlight-point-halo',
        'lyr-signals-highlight-point',
    ];
    static RADIUS_SOURCE_ID = 'src-signals-radius';
    static RADIUS_LAYER_IDS = ['lyr-signals-radius-fill', 'lyr-signals-radius-line'];
    static RENDER_ORDER_LAYER_IDS = SignalsLayer.BASE_LAYER_IDS.concat(SignalsLayer.HIGHLIGHT_LAYER_IDS, SignalsLayer.RADIUS_LAYER_IDS);
    static AGGREGATED_MARKER_COLOR = 'rgba(103,118,134,0.88)';
    static AGGREGATED_PRO_COLOR = 'rgba(16,215,88,0.9)';
    static AGGREGATED_CON_COLOR = 'rgba(181,110,84,0.9)';

    constructor(config = {}) {
        super(config);
    }

    getSourceId() {
        return SignalsLayer.SOURCE_ID;
    }

    getLayerIds() {
        return SignalsLayer.BASE_LAYER_IDS;
    }

    getRenderOrderLayerIds() {
        return SignalsLayer.RENDER_ORDER_LAYER_IDS;
    }

    buildUrl() {
        return this.config.baseUrl + 'api/signals/geojson?bbox=' + this.config.getCurrentBbox() + '&zoom=' + this.config.getCurrentZoom();
    }


    renderAggregatedData(data) {
        var map = this.getMap();
        if (!map) {
            return null;
        }

        var self = this;
        var markerGroup = new MarkerGroup();
        groupLayerMarkers(data.features, map, data.aggregated === true).forEach(function(feature) {
            if (!feature || !feature.geometry || feature.geometry.type !== 'Point') {
                return;
            }

            if (feature.properties?.aggregated !== true) {
                const marker = self.createFeatureMarker(feature);
                if (marker) markerGroup.add(marker);
                return;
            }
            var props = feature.properties || {};
            var count = Number(props.count || props.signal_count || 1);
            var proCount = Number(props.pro_count || 0);
            var conCount = Number(props.con_count || 0);
            var coords = feature.geometry.coordinates;
            var html = proCount > 0 && conCount > 0
                ? buildSignalsAggPillHtml(proCount, conCount)
                : buildSignalsAggDotHtml(count, resolveSignalsAggDotColor(proCount, conCount));

            markerGroup.add(createHtmlMarker(
                coords,
                html,
                'nm-agg-wrap',
                {
                    onClick: function() {
                        drillDownTransactionCluster(map, feature);
                    },
                }
            ));
        });

        markerGroup.addTo(map);

        return markerGroup;
    }

    createFeatureMarker(feature) {
        if (!feature || !feature.geometry || feature.geometry.type !== 'Point') {
            return null;
        }

        var self = this;
        var props = feature.properties || {};
        var coords = feature.geometry.coordinates;
        var icon = props.icon || '•';
        var sentiment = props.icon_sentiment;
        var tone = getSentimentTone(sentiment);
        var count = Number(props.signal_count || 0);

        var markerIdAttr = props.id != null ? ' data-signal-marker-id="' + props.id + '"' : '';
        var html;
        if (count > 1) {
            html = '<div class="nm-signal-marker nm-signal-marker--group"' + markerIdAttr + ' style="border-color:' + tone.marker + '">'
                + '<span class="nm-signal-marker__emoji">' + icon + '</span>'
                + '<span class="nm-signal-marker__count">' + count + '</span>'
                + '</div>';
        } else {
            html = '<div class="nm-signal-marker"' + markerIdAttr + ' style="border-color:' + tone.marker + '">'
                + '<span class="nm-signal-marker__emoji">' + icon + '</span>'
                + '</div>';
        }

        return createHtmlMarker(
            coords,
            html,
            'nm-signal-marker-wrap',
            {
                onClick: function() {
                    var latlng = { lat: coords[1], lng: coords[0] };
                    if (self.config.selectSignalHit) {
                        self.config.selectSignalHit(props, latlng);
                    }
                },
            }
        );
    }

    renderIndividualData(data) {
        var map = this.getMap();
        if (!map) {
            return null;
        }

        var self = this;
        var markerGroup = new MarkerGroup();

        (data.features || []).forEach(function(feature) {
            var marker = self.createFeatureMarker(feature);
            if (marker) {
                markerGroup.add(marker);
            }
        });

        markerGroup.addTo(map);

        return markerGroup;
    }

    renderData(data) {
        return this.renderCapacityMarkerData(data);
    }

    clearRadius() {
        var map = this.getMap();
        if (!map) {
            return;
        }

        removeSourceAndLayers(map, SignalsLayer.RADIUS_SOURCE_ID, SignalsLayer.RADIUS_LAYER_IDS);
    }

    clearGeometryHighlight() {
        var map = this.getMap();
        if (!map) {
            return;
        }

        removeSourceAndLayers(map, SignalsLayer.HIGHLIGHT_SOURCE_ID, SignalsLayer.HIGHLIGHT_LAYER_IDS);
    }

    clearHighlight() {
        this.clearGeometryHighlight();
        this.clearRadius();
    }

    showGeometryHighlight(signal) {
        var map = this.getMap();
        var colors = getHighlightColorsForSignal(signal);
        var featureCollection = normalizeGeoJsonToFeatureCollection(signal && signal.coverage_geojson);

        if (!map) {
            return;
        }

        if (!featureCollection && signal && signal.lng != null && signal.lat != null) {
            featureCollection = buildPointHighlightFeatureCollection(Number(signal.lng), Number(signal.lat));
        }

        if (!featureCollection) {
            this.clearGeometryHighlight();
            return;
        }

        addOrUpdateGeoJsonSource(map, SignalsLayer.HIGHLIGHT_SOURCE_ID, featureCollection);

        map.addLayer({
            id: SignalsLayer.HIGHLIGHT_LAYER_IDS[0],
            type: 'fill',
            source: SignalsLayer.HIGHLIGHT_SOURCE_ID,
            filter: ['==', '$type', 'Polygon'],
            paint: {
                'fill-color': colors.fill,
                'fill-opacity': 1,
            },
        });
        map.addLayer({
            id: SignalsLayer.HIGHLIGHT_LAYER_IDS[1],
            type: 'line',
            source: SignalsLayer.HIGHLIGHT_SOURCE_ID,
            filter: ['==', '$type', 'Polygon'],
            paint: {
                'line-color': colors.line,
                'line-width': 2.4,
                'line-opacity': 0.96,
            },
        });
        map.addLayer({
            id: SignalsLayer.HIGHLIGHT_LAYER_IDS[2],
            type: 'line',
            source: SignalsLayer.HIGHLIGHT_SOURCE_ID,
            filter: ['==', '$type', 'LineString'],
            paint: {
                'line-color': colors.halo,
                'line-width': 7,
                'line-opacity': 0.94,
            },
        });
        map.addLayer({
            id: SignalsLayer.HIGHLIGHT_LAYER_IDS[3],
            type: 'line',
            source: SignalsLayer.HIGHLIGHT_SOURCE_ID,
            filter: ['==', '$type', 'LineString'],
            paint: {
                'line-color': colors.line,
                'line-width': 4,
                'line-opacity': 0.98,
            },
        });
        map.addLayer({
            id: SignalsLayer.HIGHLIGHT_LAYER_IDS[4],
            type: 'circle',
            source: SignalsLayer.HIGHLIGHT_SOURCE_ID,
            filter: ['==', '$type', 'Point'],
            paint: {
                'circle-radius': 11,
                'circle-color': colors.halo,
                'circle-opacity': 0.98,
            },
        });
        map.addLayer({
            id: SignalsLayer.HIGHLIGHT_LAYER_IDS[5],
            type: 'circle',
            source: SignalsLayer.HIGHLIGHT_SOURCE_ID,
            filter: ['==', '$type', 'Point'],
            paint: {
                'circle-radius': 6.8,
                'circle-color': colors.point,
                'circle-stroke-color': colors.casing,
                'circle-stroke-width': 1.5,
                'circle-opacity': 0.98,
            },
        });
    }

    showSignalHighlight(signal) {
        var map = this.getMap();
        if (!map || !signal || !signal.category) {
            this.clearHighlight();
            return;
        }

        this.clearHighlight();

        if (signal.highlight_mode && signal.highlight_mode !== 'radius') {
            this.showGeometryHighlight(signal);
            return;
        }

        var radiusMeters = Number(signal.influence_radius_m || 0);
        var colors = getRadiusColorsForSignal(signal);
        if (!colors || !isFinite(radiusMeters) || radiusMeters <= 0 || signal.lat == null || signal.lng == null) {
            this.showGeometryHighlight(signal);
            return;
        }

        addOrUpdateGeoJsonSource(
            map,
            SignalsLayer.RADIUS_SOURCE_ID,
            buildRadiusPolygon(Number(signal.lng), Number(signal.lat), radiusMeters, 64)
        );

        map.addLayer({
            id: SignalsLayer.RADIUS_LAYER_IDS[0],
            type: 'fill',
            source: SignalsLayer.RADIUS_SOURCE_ID,
            paint: {
                'fill-color': colors.fill,
                'fill-opacity': 1,
            },
        });
        map.addLayer({
            id: SignalsLayer.RADIUS_LAYER_IDS[1],
            type: 'line',
            source: SignalsLayer.RADIUS_SOURCE_ID,
            paint: {
                'line-color': colors.line,
                'line-width': 2.5,
                'line-opacity': 0.95,
            },
        });
    }

    showSignalRadius(signal) {
        this.showSignalHighlight(signal);
    }

    clear() {
        this.abortPending();
        this.resetAccumulator();
        this.disposeKeyedMarkerGroup();

        var map = this.getMap();
        var layer = this.config.getLayer ? this.config.getLayer() : null;
        if (layer && typeof layer.remove === 'function') {
            layer.remove();
        }
        if (map) {
            removeSourceAndLayers(map, this.getSourceId(), this.getLayerIds());
        }

        this.clearHighlight();

        if (this.config.setLayer) {
            this.config.setLayer(null);
        }

        if (this.config.onCleared) {
            this.config.onCleared(this);
        }
    }
}